import SkuModel from 'volta/models/sku';
import Ember from 'ember';
import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
// @ts-ignore
import ActionCell from 'volta/components/cell/action-cell';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
import { Events, TransactionResourceName } from 'volta/models/transaction';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { classNames } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type BvMetricsService from 'volta/services/bv-metrics';
import type StoreService from 'volta/services/store';
import type {
  ITransactionAgg,
  ITransactionResponse,
  ITransactionsByDayUpdated
} from 'volta/models/types/transaction';
import type EventStreamService from 'volta/services/event-stream';

interface IArgs {
  skuId: string;
  sku?: SkuModel;
  unitMeasure?: string;
  close?: () => void;
  switchTab?: () => void;
  showTransactionsModal?: (t: ITransactionAgg) => void;
}
export default class SkuDetailsTransactions extends Component<IArgs> {
  // Services
  // ~~~~~

  @service store!: StoreService;
  @service intl!: IntlService;
  @service bvMetrics!: BvMetricsService;
  @service eventStream!: EventStreamService;

  // Properties
  // ~~~~~

  @tracked sorts = [{ valuePath: 'transactionDate', isAscending: false }];
  @tracked transactions: ITransactionAgg[] = [];
  @tracked showCopyTransactionsModal: boolean = false;
  @tracked showCopyTransactionsFromModal: boolean = false;
  @tracked selectedTransactionDate?: string;
  @tracked showTransactions = false;
  resourceName = TransactionResourceName;

  // Computed Properties
  // ~~~~~

  @sort('transactions', 'sortProperties')
  declare sortedTransactions: ITransactionAgg[];

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get columns(): IBvTableColumn[] {
    const intl = this.intl;

    return [
      {
        columnName: intl.t('week'),
        valuePath: 'week',
        textAlign: 'right',
        width: 50,
        isReorderable: true,
        isResizable: false,
        cellComponent: FormatCell,
        format: (model: ITransactionAgg) => {
          const isCurrentWeek = moment.utc().isSame(moment.utc(model.transactionDate), 'week');
          return {
            value: model.transactionDate,
            dataType: 'date',
            format: 'w',
            cellClasses: classNames([isCurrentWeek && 'u-text-bolder'])
          };
        }
      },
      {
        columnName: intl.t('transactionDate'),
        valuePath: 'transactionDate',
        width: 80,
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: ITransactionAgg) => {
          return {
            value: model.transactionDate,
            dataType: 'date'
          };
        }
      },
      {
        columnName: intl.t('incoming'),
        valuePath: 'incoming',
        width: 75,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        format: (model: ITransactionAgg) => {
          const incoming = model.incoming;
          return isPresent(incoming) ? intl.formatNumber(incoming) : null;
        }
      },
      {
        columnName: intl.t('outgoing'),
        valuePath: 'outgoing',
        width: 75,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        format: (model: ITransactionAgg) => {
          const outgoing = model.outgoing;
          return isPresent(outgoing) ? intl.formatNumber(outgoing) : null;
        }
      },
      {
        columnName: intl.t('onHand'),
        valuePath: 'onHand',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: ITransactionAgg) => {
          return {
            value: model.onHand,
            dataType: 'number'
          };
        }
      },
      {
        columnName: intl.t('details'),
        valuePath: 'count',
        sortKey: 'count',
        textAlign: 'center',
        isReorderable: false,
        isResizable: false,
        cellComponent: ActionCell,
        showOnHover: true,
        format: (model: ITransactionAgg) => {
          return {
            model: model.transactionDate,
            context: this,
            visible: true,
            text: model.count,
            action: (m: string) => this.showTransactionsModal(m),
            iconRight: 'search',
            size: 'sm',
            tooltip: intl.t('showTransactions'),
            classNames: 'u-text-mono'
          };
        }
      }
    ];
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      'stats',
      Events.TransactionsByDayUpdated,
      this.handleTransactionsByDayUpdatedEvent,
      this
    );
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  @action
  handleDidInsert() {
    this.getTransactions.perform(this.args.skuId);
  }

  @action
  showTransactionsModal(transactionDate: string) {
    this.bvMetrics.trackAction(this, 'showTransactionsModal');
    this.selectedTransactionDate = transactionDate;
    this.showTransactions = true;
  }

  @action
  hideTransactionsModal() {
    this.bvMetrics.trackAction(this, 'hideTransactionsModal');
    this.selectedTransactionDate = undefined;
    this.showTransactions = false;
  }

  @action
  async onTransactionsCopyFromModalClose(data?: ITransactionResponse) {
    this.showCopyTransactionsFromModal = false;
    if (data) {
      await this.handleDidInsert();
    }
  }

  // Tasks
  // ~~~~~

  getTransactions = restartableTask(async (skuId: string) => {
    try {
      const response: Ember.Array<ITransactionAgg> = await this.store.customQuery(
        'sku',
        'transactionsAgg',
        skuId
      );
      if (response && response.length) {
        this.transactions = response.toArray();
      } else {
        this.transactions = [];
      }
      return this.transactions;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  // Private functions
  // ~~~~~~

  private async handleTransactionsByDayUpdatedEvent(payload: ITransactionsByDayUpdated) {
    if (
      this.args.skuId &&
      (payload.skuIds.includes(this.args.skuId) ||
        payload.warehouseIds.includes(this.args.sku?.warehouse.warehouseId!))
    ) {
      this.getTransactions.perform(this.args.skuId);
    }
  }
}
